import { AlarmSignalR } from 'domain/alarm-signal-r'
import { getAlarmTypeLabel } from 'features/alarms/types/signal-r-alarm-type.helpers'
import {
  formatAlarmDate,
  getCriterionTypeLabel,
  renderCriterionValue,
} from 'features/alarms/utils/alarm-signal-r-display'
import { AnprCriterionTypeEnum } from 'features/alarms/types/alarm.types'
import { useSignalR } from 'layout/context/signalr-context'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Fragment, useEffect, useRef, useState } from 'react'
import MercosulPlate from 'shared/mercosul-plate/mercosul-license-plate'
import './alarm-dialog.scss'

/**
 * Full-attention dialog for an incoming alarm. Mounted at the app root so it
 * fires on whichever screen the operator happens to be on.
 */
const AlarmDialog = () => {
  const { latestAlarm } = useSignalR()

  const [alarm, setAlarm] = useState<AlarmSignalR | null>(null)
  const [visible, setVisible] = useState(false)

  // Compared by object identity, not content: two genuinely distinct alarms can
  // carry identical payloads, and the SignalR handler always sets a fresh object.
  // This only suppresses the replay a remount would otherwise cause.
  const lastShownAlarmRef = useRef<AlarmSignalR | null>(null)

  useEffect(() => {
    if (!latestAlarm || latestAlarm === lastShownAlarmRef.current) {
      return
    }

    lastShownAlarmRef.current = latestAlarm
    setAlarm(latestAlarm)
    setVisible(true)
  }, [latestAlarm])

  if (!alarm) {
    return null
  }

  const alarmType = getAlarmTypeLabel(alarm.type)
  const when = formatAlarmDate(alarm.alarmDate)
  const criterias = alarm.criterias ?? []

  // The plate is shown as a plate, so it doesn't repeat in the detail list.
  const plate = criterias.find(
    (criteria) => criteria.type === AnprCriterionTypeEnum.LICENSE_PLATE
  )
  const otherCriterias = criterias.filter((criteria) => criteria !== plate)

  const header = (
    <div className='alarm-dialog__header'>
      <i className='pi pi-exclamation-triangle' />
      <span>Alarme {alarmType}</span>
    </div>
  )

  const footer = (
    <div className='alarm-dialog__footer'>
      <Button
        label='Enviar para WhatsApp'
        icon='pi pi-whatsapp'
        severity='success'
        disabled
        tooltip='Número de WhatsApp não está configurado'
        tooltipOptions={{ position: 'top', showOnDisabled: true }}
      />
      <Button
        label='Fechar'
        icon='pi pi-times'
        severity='secondary'
        outlined
        onClick={() => setVisible(false)}
      />
    </div>
  )

  return (
    <Dialog
      header={header}
      footer={footer}
      visible={visible}
      onHide={() => setVisible(false)}
      className='alarm-dialog'
      style={{ width: '32rem' }}
      breakpoints={{ '640px': '95vw' }}
      dismissableMask
      draggable={false}
    >
      {alarm.alarmImageUrl && (
        <img
          className='alarm-dialog__image'
          src={alarm.alarmImageUrl}
          alt={`Evidência do alarme ${alarmType}`}
        />
      )}

      {plate && (
        <MercosulPlate
          licensePlate={plate.value}
          size='M'
        />
      )}

      <dl className='alarm-dialog__details'>
        {alarm.cameraName && (
          <>
            <dt>Câmera</dt>
            <dd>{alarm.cameraName}</dd>
          </>
        )}

        {when && (
          <>
            <dt>Data</dt>
            <dd>{when}</dd>
          </>
        )}

        {otherCriterias.map((criteria, index) => (
          <Fragment key={`${criteria.type}-${index}`}>
            <dt>{getCriterionTypeLabel(criteria.type, false)}</dt>
            <dd>{renderCriterionValue(criteria)}</dd>
          </Fragment>
        ))}
      </dl>
    </Dialog>
  )
}

export default AlarmDialog
